import moment from 'moment';
import React from 'react';

import App from '../Config/App';
import { ErrorPlacement } from '../components/common/inputs/interfaces/ValidatedInputProps';
import { LotteryTypes } from '../components/play/Types/Enums';

import { isAuthModalEnabled, isDefaultCasino } from './template';

import { LotteryType } from 'Api/CRM/Responses/LotteryResponse';
import Config from 'Config/Config';
import { pageLanguage } from 'components/common/Route';
import { LineNumbers, LotteryRanges, LotteryRuleTier, RulesPickConfigType } from 'components/play/Types/Types';

/**
 * assumes one number per pool
 *
 * @param ranges - lottery ranges
 * @param numbersToPick - amount of numbers to generate
 */
export function generateRandomLine(ranges: LotteryRanges, numbersToPick: number): LineNumbers {
    const random = [];

    for (let i = 0; i < numbersToPick; i++) {
        const start = ranges[i][0];
        const end = ranges[i][1];
        const value = Math.floor(Math.random() * (end + 1) + start);

        random.push([value]);
    }

    return random;
}

/**
 * Add leading zeros to number. e.g. from 648 to 000648
 */
export function padNumber(number: number | string, padLength: number) {
    return number.toString().padStart(padLength, '0');
}

/**
 * Rounds a number to a given number of decimals.
 * Defaults to 2 decimals if not specified.
 */
export function roundToDecimals(value: number | string, decimals: number = 2): number {
    return +parseFloat(`${value}`).toFixed(decimals);
}

export function formatNumber(number: number, lotteryType: LotteryType, padLength: number | null = null): string {
    if ([LotteryTypes.RaffleOpen, LotteryTypes.RaffleClosed].includes(lotteryType as LotteryTypes)) {
        return padNumber(number, padLength);
    }

    return number.toString();
}

export function isDoubleChanceLottery(slug: string): boolean {
    return ['nap-2-double-chance-cameroon'].includes(slug);
}

export function isDatePast(date: string): boolean {
    const now = new Date();
    const dateToCheck = new Date(date);

    return dateToCheck < now;
}

export function triggerDeprecation(message: string) {
    if (!App.isProduction()) {
        console.trace(`Deprecation: ${message}`);
    }
}

/**
 * Add dusk tag for testing purposes
 * Usage: <div {...dusk('test-dusk-id')}></div> will produce HTML like that <div dusk="test"></div>
 *
 * @param {string} tag
 * @return {object} an empty object if env is invalid (local and testing are OK)
 */
export function dusk(tag: string): object {
    if (!App.isProduction()) {
        return { 'data-dusk': tag };
    }

    return {};
}

export function numberArray(length: number, inclusive = false): number[] {
    const adjustedLength = inclusive ? length + 1 : length;
    return adjustedLength >= 0 ? [...Array(adjustedLength).keys()] : [];
}

export function rangeArray(from: number, to: number): number[] {
    return to >= from ? [...Array(to - from + 1).keys()].map((i) => i + from) : [];
}

export function randomNumber(from: number, to: number): number {
    return Math.floor(Math.random() * (to - from + 1) + from);
}

export function getMobileOperatingSystem(): 'Android' | 'iOS' | 'unknown' {
    if (/android/i.test(navigator.userAgent)) return 'Android';
    if (/iPad|iPhone/.test(navigator.userAgent)) return 'iOS';
    return 'unknown';
}

export function isInWebView(): boolean {
    return navigator.userAgent.includes('WebView');
}

export function updateInnerHTML(ref: React.RefObject<HTMLElement>, content: string): void {
    if (ref?.current) {
        ref.current.innerHTML = content;
    }
}

export function getVisibleProviderName(providerName: string): string {
    if (providerName === 'twitter') {
        return 'X';
    }
    if (providerName === 'partner') {
        return process.env.MIX_PARTNER_CLIENT_NAME !== '' ? process.env.MIX_PARTNER_CLIENT_NAME : providerName;
    }
    return providerName;
}

export function convertUtcToUserTimezone(utcDate: string | Date): string {
    const date = typeof utcDate === 'string' ? utcDate : utcDate.toISOString();
    return moment.tz(date, 'UTC').tz(moment.tz.guess()).format('YYYY-MM-DD HH:mm:ss');
}

// Use for datetime-local input
export function convertToLocalDateTimeString(utcDate: string | Date): string {
    const date = typeof utcDate === 'string' ? utcDate : utcDate.toISOString();
    return moment.tz(date, 'UTC').tz(moment.tz.guess()).format('YYYY-MM-DDTHH:mm');
}

const usesCommaAsDecimal = new Intl.NumberFormat(pageLanguage || Config.get('app.locale'), { minimumFractionDigits: 1 })
    .format(1.1)
    .includes(',');

export function formatDepositInput(input: string): string {
    return usesCommaAsDecimal ? input.replace('.', ',') : input;
}

export function goBack() {
    window.history.back();
}

export function arrayWith<T>(array: T[], index: number, value: T): T[] {
    if (index < 0 || index >= array.length) {
        throw new RangeError('Index out of bounds');
    }
    return array.map((item, i) => (i === index ? value : item));
}

export function isPlainObject(value: unknown): value is Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value);
}

export function safeJsonParse<T>(value: string | null, defaultValue: T | null = null): T | null {
    if (!value) return defaultValue;

    try {
        const parsed = JSON.parse(value);
        return parsed === null ? defaultValue : (parsed as T);
    } catch {
        return defaultValue;
    }
}

export const findNearestDate = (date: string, dates: string[]): string => {
    return dates.reduce((nearest, current) => {
        const currentMoment = moment(current);
        const nearestMoment = moment(nearest);
        const dateMoment = moment(date);

        if (currentMoment.isBefore(dateMoment) && currentMoment.isAfter(nearestMoment)) {
            return current;
        }
        if (currentMoment.isAfter(dateMoment) && currentMoment.isBefore(nearestMoment)) {
            return current;
        }

        return nearest;
    }, dates[0]);
};

export const generateRandomNumbersForRules = (tier: LotteryRuleTier): number[] => {
    const firstWinExample = tier.win_example[0];

    if (!Array.isArray(firstWinExample)) {
        return [];
    }

    const length = firstWinExample.length;
    const availableNumbers = [...Array(10).keys()];

    const randomNumbers: number[] = [];
    for (let i = 0; i < length; i++) {
        const randomIndex = Math.floor(Math.random() * availableNumbers.length);
        randomNumbers.push(availableNumbers[randomIndex]);
        availableNumbers.splice(randomIndex, 1);
    }

    return randomNumbers;
};

export const mapCombinationsForRules = (yourPick: number[], winExamples: number[][]) => {
    return winExamples.map((combination) => combination.map((value) => yourPick[value] || 0));
};

export const generateNumbersForRules = (
    ruleConfig: RulesPickConfigType[keyof RulesPickConfigType],
    tier: LotteryRuleTier
): number[] => {
    switch (ruleConfig.behavior) {
        case 'useFirstWinExample':
            return Array(tier.win_example[0]?.length).fill(1);
        case 'incrementalNumbers':
            return tier.win_example[0]?.filter((value) => typeof value === 'number').map((_, index) => index) || [];
        case 'frontBackNumbers':
            return Array.from({ length: tier.numbers_to_pick ?? 0 }, (_, index) => index);
        case 'generateRandomNumbers':
            return generateRandomNumbersForRules(tier);
        default:
            return [];
    }
};

export const getRandomNumbersForRaffle = (
    allNumbers: number[],
    selectedNumbers: number[],
    freeNumbers: number[],
    count: number
): number[] => {
    const availableNumbers = allNumbers.filter((num) => !selectedNumbers.includes(num) && freeNumbers.includes(num));
    return availableNumbers.sort(() => 0.5 - Math.random()).slice(0, count);
};

export const toTitleCase = (str: string) =>
    str.replace(
        /\p{L}[\p{L}\p{M}']*/gu,
        (word) => word.charAt(0).toLocaleUpperCase() + word.slice(1).toLocaleLowerCase()
    );

export const cleanRoute = (route: string): string => (route ? route.replace(/\/placeholder\/?/, '') || '' : '');

/**
 * Returns true when the provided jackpot/prizes indicate an in-kind prize for the top tier.
 *
 * Usage examples:
 * - isJackpotPrizeInKind({ jackpot: lottery?.lottery?.jackpot ?? lottery?.last_draw?.jackpot })
 * - isJackpotPrizeInKind({ prizes })
 */
export function isJackpotPrizeInKind(params: {
    jackpot?: unknown | null | undefined;
    prizes?: { name?: string | null } | Record<string, unknown>[] | null | undefined;
}): boolean {
    const jackpot = params?.jackpot;
    const prizes = params?.prizes;

    // Prefer explicit jackpot check when provided (object with a non-empty `name` field)
    if (jackpot !== undefined) {
        if (jackpot !== null && typeof jackpot === 'object' && hasNonEmptyName((jackpot as any).name)) {
            return true;
        }
    }

    // Fallback: check the top prize in prizes array (first element) for a non-empty `name`
    if (Array.isArray(prizes) && prizes.length > 0) {
        const topPrize: any = prizes[0];
        if (hasNonEmptyName(topPrize?.name)) {
            return true;
        }
    }

    return false;
}

function hasNonEmptyName(value: unknown): boolean {
    return typeof value === 'string' && value.trim().length > 0;
}

export function isTrueLike(value: unknown) {
    return ['1', 'true'].includes(String(value).toLowerCase());
}

export function getErrorPlacement(): ErrorPlacement {
    if (isDefaultCasino()) {
        return 'below';
    }

    return isAuthModalEnabled() ? 'inline' : 'below';
}
